import axios from 'axios';
import { getApiBaseUrl } from '../utils/getApiUrl.ts';
import { getRecurringTransactionById, markRecurringSynced } from './db/repositories/recurringTransactions.ts';
import { getAccountById, listAccounts, markSynced as markAccountSynced } from './db/repositories/accounts.ts';
import { getCategoryById, listCategories, markSynced as markCategorySynced } from './db/repositories/categories.ts';
import { getTransactionById, markSynced as markTransactionSynced } from './db/repositories/transactions.ts';
import { getTaskById, markSynced as markTaskSynced } from './db/repositories/tasks.ts';
import { getObjectiveById, markObjectiveSynced } from './db/repositories/okrObjectives.ts';
import { getKeyResultById, listKeyResultsByObjective, markKeyResultSynced } from './db/repositories/okrKeyResults.ts';
import { getIndicatorById, markIndicatorSynced } from './db/repositories/okrIndicators.ts';
import { getInvestmentPlanById, markInvestmentPlanSynced } from './db/repositories/investmentPlans.ts';
import { getInvestmentScenarioById, markInvestmentScenarioSynced } from './db/repositories/investmentScenarios.ts';
import { getInvestmentLedgerById, markInvestmentLedgerSynced } from './db/repositories/investmentLedgers.ts';
import { getInvestmentRecordById, markInvestmentRecordSynced } from './db/repositories/investmentRecords.ts';
import { getInvestmentAllocationById, markInvestmentAllocationSynced } from './db/repositories/investmentAllocations.ts';
import { getInvestmentGoalById, markInvestmentGoalSynced } from './db/repositories/investmentGoals.ts';
import { deleteSyncItem, enqueueSyncItem, listPendingSyncItems, updateSyncItem, SyncQueueItem, SyncQueueEntityRef } from './db/repositories/syncQueue.ts';

const API_BASE_URL = getApiBaseUrl();
const MAX_ATTEMPTS = 5;

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const RECURRING_ENDPOINT = /^\/api\/v1\/recurring-transactions(\/\d+)?$/;
const RECURRING_DELETE_SCOPE = /^\/api\/v1\/recurring-transactions\/\d+\/delete-scope$/;
const ACCOUNT_ENDPOINT = /^\/api\/v1\/accounts(\/\d+)?$/;
const CATEGORY_ENDPOINT = /^\/api\/v1\/categories(\/\d+)?$/;
const TRANSACTION_ENDPOINT = /^\/api\/v1\/transactions(\/\d+)?$/;
const TRANSACTION_MARK_PAID = /^\/api\/v1\/transactions\/\d+\/mark-paid$/;
const TRANSACTION_MARK_UNPAID = /^\/api\/v1\/transactions\/\d+\/mark-unpaid$/;
const TASK_ENDPOINT = /^\/api\/v1\/tasks(\/\d+)?$/;
const TASK_COMPLETE = /^\/api\/v1\/tasks\/\d+\/complete$/;
const TASK_UNCOMPLETE = /^\/api\/v1\/tasks\/\d+\/uncomplete$/;
const OBJECTIVE_ENDPOINT = /^\/api\/v1\/objectives(\/\d+)?$/;
const KEY_RESULT_ENDPOINT = /^\/api\/v1\/key-results(\/\d+)?$/;
const INDICATOR_ENDPOINT = /^\/api\/v1\/key-results\/\d+\/indicators$/;
const INVESTMENT_PLAN_ENDPOINT = /^\/api\/v1\/investment-plans(\/\d+)?$/;
const INVESTMENT_LEDGER_ENDPOINT = /^\/api\/v1\/investment-tracking\/ledgers$/;
const INVESTMENT_RECORD_ENDPOINT = /^\/api\/v1\/investment-tracking\/records$/;
const INVESTMENT_SCENARIO_ENDPOINT = /^\/api\/v1\/investment-advisory\/scenarios$/;
const INVESTMENT_ALLOCATION_ENDPOINT = /^\/api\/v1\/investment-advisory\/allocation$/;
const INVESTMENT_GOAL_ENDPOINT = /^\/api\/v1\/investment-advisory\/goals$/;

export function shouldEnqueueSyncRequest(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', url: string): boolean {
  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    return false;
  }
  return (
    RECURRING_ENDPOINT.test(url) ||
    RECURRING_DELETE_SCOPE.test(url) ||
    ACCOUNT_ENDPOINT.test(url) ||
    CATEGORY_ENDPOINT.test(url) ||
    TRANSACTION_ENDPOINT.test(url) ||
    TRANSACTION_MARK_PAID.test(url) ||
    TRANSACTION_MARK_UNPAID.test(url) ||
    TASK_ENDPOINT.test(url) ||
    TASK_COMPLETE.test(url) ||
    TASK_UNCOMPLETE.test(url) ||
    OBJECTIVE_ENDPOINT.test(url) ||
    KEY_RESULT_ENDPOINT.test(url) ||
    INDICATOR_ENDPOINT.test(url) ||
    INVESTMENT_PLAN_ENDPOINT.test(url) ||
    INVESTMENT_LEDGER_ENDPOINT.test(url) ||
    INVESTMENT_RECORD_ENDPOINT.test(url) ||
    INVESTMENT_SCENARIO_ENDPOINT.test(url) ||
    INVESTMENT_ALLOCATION_ENDPOINT.test(url) ||
    INVESTMENT_GOAL_ENDPOINT.test(url)
  );
}

export async function enqueueSyncRequest(params: {
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  body?: any;
  entity?: SyncQueueEntityRef;
}) {
  if (!shouldEnqueueSyncRequest(params.method, params.url)) {
    return;
  }
  await enqueueSyncItem({
    method: params.method,
    url: params.url,
    body: params.body,
    entity: params.entity,
  });
}

function parseLocalIdFromUrl(url: string): number | null {
  const match = url.match(/\/(\d+)(?:\/|$)/);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) ? id : null;
}

async function resolveAccountServerId(localId: number): Promise<number | null> {
  const account = await getAccountById(localId);
  if (account?.server_id) return account.server_id;
  const all = await listAccounts();
  const byServerId = all.find((item) => item.server_id === localId);
  if (byServerId?.server_id) return byServerId.server_id;
  return null;
}

async function resolveCategoryServerId(localId: number): Promise<number | null> {
  const category = await getCategoryById(localId);
  if (category?.server_id) return category.server_id;
  const all = await listCategories();
  const byServerId = all.find((item) => item.server_id === localId);
  if (byServerId?.server_id) return byServerId.server_id;
  return null;
}

async function resolveTransactionServerId(localId: number): Promise<number | null> {
  const transaction = await getTransactionById(localId);
  return transaction?.server_id ?? null;
}

async function resolveRecurringServerId(localId: number): Promise<number | null> {
  const recurring = await getRecurringTransactionById(localId);
  return recurring?.server_id ?? null;
}

async function resolveKeyResultServerId(localId: number): Promise<number | null> {
  const keyResult = await getKeyResultById(localId);
  return keyResult?.server_id ?? null;
}

async function resolveInvestmentPlanServerId(localId: number): Promise<number | null> {
  const plan = await getInvestmentPlanById(localId);
  return plan?.server_id ?? null;
}

async function resolveInvestmentLedgerServerId(localId: number): Promise<number | null> {
  const ledger = await getInvestmentLedgerById(localId);
  return ledger?.server_id ?? null;
}

async function buildAccountPayload(localId: number) {
  const account = await getAccountById(localId);
  if (!account) {
    return { missing: true as const };
  }
  return {
    account,
    payload: {
      name: account.name,
      description: account.description,
      balance: account.balance,
      is_default: account.is_default ?? false,
      is_investment: account.is_investment ?? false,
    },
  };
}

async function buildCategoryPayload(localId: number) {
  const category = await getCategoryById(localId);
  if (!category) {
    return { missing: true as const };
  }
  return {
    category,
    payload: {
      name: category.name,
      description: category.description,
      type: category.type,
      icon: category.icon,
      icon_color: category.icon_color,
      is_default: category.is_default ?? false,
    },
  };
}

async function buildTransactionPayload(localId: number) {
  const transaction = await getTransactionById(localId);
  if (!transaction) {
    return { missing: true as const };
  }
  const accountServerId = (await resolveAccountServerId(transaction.account_id)) ?? transaction.account_id;
  const categoryServerId = (await resolveCategoryServerId(transaction.category_id)) ?? transaction.category_id;
  if (!accountServerId || !categoryServerId) {
    return { defer: true as const };
  }
  let destinationAccountServerId: number | undefined;
  if (transaction.destination_account_id) {
    destinationAccountServerId =
      (await resolveAccountServerId(transaction.destination_account_id)) ?? transaction.destination_account_id;
  }
  let recurringServerId: number | undefined;
  if (transaction.recurring_transaction_id) {
    recurringServerId = await resolveRecurringServerId(transaction.recurring_transaction_id);
    if (!recurringServerId) {
      return { defer: true as const };
    }
  }
  return {
    transaction,
    payload: {
      description: transaction.description,
      amount: transaction.amount,
      type: transaction.type,
      due_date: transaction.due_date,
      is_paid: transaction.is_paid,
      paid_date: transaction.paid_date,
      account_id: accountServerId,
      destination_account_id: destinationAccountServerId,
      category_id: categoryServerId,
      recurring_transaction_id: recurringServerId,
    },
  };
}

async function buildTaskPayload(localId: number) {
  const task = await getTaskById(localId);
  if (!task) {
    return { missing: true as const };
  }
  let transactionServerId: number | null = null;
  if (task.transaction_id) {
    transactionServerId = await resolveTransactionServerId(task.transaction_id);
    if (!transactionServerId) {
      return { defer: true as const };
    }
  }
  return {
    task,
    payload: {
      title: task.title,
      description: task.description,
      due_date: task.due_date,
      is_urgent: task.is_urgent,
      is_important: task.is_important,
      status: task.status,
      transaction_id: transactionServerId || null,
    },
  };
}

async function buildRecurringPayload(localId: number) {
  const recurring = await getRecurringTransactionById(localId);
  if (!recurring) {
    return { missing: true as const };
  }
  const accountServerId = await resolveAccountServerId(recurring.account_id);
  const categoryServerId = await resolveCategoryServerId(recurring.category_id);
  if (!accountServerId || !categoryServerId) {
    return { defer: true as const };
  }
  return {
    recurring,
    payload: {
      description: recurring.description,
      amount: recurring.amount,
      type: recurring.type,
      account_id: accountServerId,
      category_id: categoryServerId,
      recurrence_type: recurring.recurrence_type,
      start_date: recurring.start_date,
      end_date: recurring.end_date || undefined,
      installments: recurring.installments || undefined,
      is_active: recurring.is_active,
    },
  };
}

async function buildObjectivePayload(localId: number) {
  const objective = await getObjectiveById(localId);
  if (!objective) {
    return { missing: true as const };
  }
  const keyResults = await listKeyResultsByObjective(localId);
  return {
    objective,
    payload: {
      title: objective.title,
      description: objective.description,
      period_type: objective.period_type,
      period_start: objective.period_start,
      period_end: objective.period_end,
      key_results: keyResults.map((kr) => ({
        title: kr.title,
        target_value: kr.target_value,
        unit: kr.unit,
      })),
    },
  };
}

async function buildObjectiveUpdatePayload(localId: number) {
  const objective = await getObjectiveById(localId);
  if (!objective) {
    return { missing: true as const };
  }
  return {
    objective,
    payload: {
      title: objective.title,
      description: objective.description,
      status: objective.status,
    },
  };
}

async function buildKeyResultPayload(localId: number) {
  const keyResult = await getKeyResultById(localId);
  if (!keyResult) {
    return { missing: true as const };
  }
  return {
    keyResult,
    payload: {
      title: keyResult.title,
      target_value: keyResult.target_value,
      unit: keyResult.unit,
      status: keyResult.status,
    },
  };
}

async function buildIndicatorPayload(localId: number) {
  const indicator = await getIndicatorById(localId);
  if (!indicator) {
    return { missing: true as const };
  }
  return {
    indicator,
    payload: {
      month: indicator.month,
      value: indicator.value,
    },
  };
}

async function buildInvestmentPlanPayload(localId: number) {
  const plan = await getInvestmentPlanById(localId);
  if (!plan) {
    return { missing: true as const };
  }
  return {
    plan,
    payload: {
      name: plan.name,
      current_age: plan.current_age,
      withdrawal_start_age: plan.withdrawal_start_age,
      life_expectancy: plan.life_expectancy,
      initial_net_worth: plan.initial_net_worth,
      monthly_contribution: plan.monthly_contribution,
      withdrawal_amount: plan.withdrawal_amount,
      expected_nominal_annual_return: plan.expected_nominal_annual_return,
      expected_annual_inflation: plan.expected_annual_inflation,
    },
  };
}

async function buildInvestmentScenarioPayload(localId: number) {
  const scenario = await getInvestmentScenarioById(localId);
  if (!scenario) {
    return { missing: true as const };
  }
  const planServerId = await resolveInvestmentPlanServerId(scenario.plan_id);
  if (!planServerId) {
    return { defer: true as const };
  }
  return {
    scenario,
    payload: {
      plan_id: planServerId,
      name: scenario.name,
      return_adjustment: scenario.return_adjustment,
      inflation_adjustment: scenario.inflation_adjustment,
      retirement_age_adjustment: scenario.retirement_age_adjustment,
      notes: scenario.notes,
    },
  };
}

async function buildInvestmentLedgerPayload(localId: number) {
  const ledger = await getInvestmentLedgerById(localId);
  if (!ledger) {
    return { missing: true as const };
  }
  return {
    ledger,
    payload: {
      name: ledger.name,
      start_month: ledger.start_month,
      end_month: ledger.end_month,
      currency: ledger.currency,
    },
  };
}

async function buildInvestmentRecordPayload(localId: number) {
  const record = await getInvestmentRecordById(localId);
  if (!record) {
    return { missing: true as const };
  }
  const ledgerServerId = await resolveInvestmentLedgerServerId(record.ledger_id);
  if (!ledgerServerId) {
    return { defer: true as const };
  }
  return {
    record,
    payload: {
      ledger_id: ledgerServerId,
      reference_month: record.reference_month,
      opening_balance: record.opening_balance,
      contributions: record.contributions,
      withdrawals: record.withdrawals,
      closing_balance: record.closing_balance,
      data_source_note: record.data_source_note,
    },
  };
}

async function buildInvestmentAllocationPayload(localId: number) {
  const allocation = await getInvestmentAllocationById(localId);
  if (!allocation) {
    return { missing: true as const };
  }
  return {
    allocation,
    payload: {
      risk_profile: allocation.risk_profile,
      asset_classes: allocation.asset_classes,
      target_allocations: allocation.target_allocations,
      current_allocations: allocation.current_allocations,
      last_reviewed_date: allocation.last_reviewed_date,
    },
  };
}

async function buildInvestmentGoalPayload(localId: number) {
  const goal = await getInvestmentGoalById(localId);
  if (!goal) {
    return { missing: true as const };
  }
  let planServerId: number | undefined;
  if (goal.plan_id) {
    planServerId = await resolveInvestmentPlanServerId(goal.plan_id);
    if (!planServerId) {
      return { defer: true as const };
    }
  }
  return {
    goal,
    payload: {
      name: goal.name,
      target_amount: goal.target_amount,
      target_date: goal.target_date,
      priority: goal.priority,
      plan_id: planServerId,
    },
  };
}

function shouldSkipSynced(record?: { needs_sync?: boolean; last_synced_at?: string; updated_at?: string }) {
  if (!record) return false;
  if (record.needs_sync) return false;
  if (!record.last_synced_at || !record.updated_at) return false;
  return record.updated_at <= record.last_synced_at;
}

async function processRecurringItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const url = item.url;
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(url);
  const serverId = item.entity?.server_id;

  if (RECURRING_DELETE_SCOPE.test(url)) {
    const resolvedLocalId = localId ?? null;
    let resolvedServerId = serverId ?? null;
    if (!resolvedServerId && resolvedLocalId) {
      const localRecurring = await getRecurringTransactionById(resolvedLocalId);
      resolvedServerId = localRecurring?.server_id ?? null;
    }
    if (!resolvedServerId) {
      return { skip: true };
    }
    await api.post(`/api/v1/recurring-transactions/${resolvedServerId}/delete-scope`, item.body || {});
    return { success: true };
  }

  if (!localId) {
    return { skip: true };
  }

  const payloadData = await buildRecurringPayload(localId);
  if (payloadData?.missing) {
    return { skip: true };
  }
  if (payloadData?.defer) {
    return { defer: true };
  }

  const { recurring, payload } = payloadData as { recurring: any; payload: any };
  if (shouldSkipSynced(recurring) && item.method !== 'DELETE') {
    return { skip: true };
  }
  if (item.method === 'POST') {
    if (recurring.server_id) {
      return { success: true };
    }
    const response = await api.post('/api/v1/recurring-transactions', payload);
    if (response.data?.id) {
      await markRecurringSynced(localId, response.data.id);
    }
    return { success: true };
  }

  const effectiveServerId = recurring.server_id || serverId;
  if (!effectiveServerId) {
    return { defer: true };
  }

  if (item.method === 'DELETE') {
    await api.delete(`/api/v1/recurring-transactions/${effectiveServerId}`);
    return { success: true };
  }

  await api.request({
    method: item.method,
    url: `/api/v1/recurring-transactions/${effectiveServerId}`,
    data: payload,
  });
  await markRecurringSynced(localId, effectiveServerId);
  return { success: true };
}

async function processObjectiveItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (!localId) return { skip: true };
  if (item.method === 'POST') {
    const payloadData = await buildObjectivePayload(localId);
    if (payloadData?.missing) return { skip: true };
    const { objective, payload } = payloadData as { objective: any; payload: any };
    if (objective.server_id) return { success: true };
    const response = await api.post('/api/v1/objectives', payload);
    const serverObjectiveId = response.data?.id;
    if (!serverObjectiveId) return { defer: true };
    await markObjectiveSynced(localId, serverObjectiveId);
    try {
      const detail = await api.get(`/api/v1/objectives/${serverObjectiveId}`);
      const serverKeyResults = detail.data?.key_results || [];
      const localKeyResults = await listKeyResultsByObjective(localId);
      for (const localKr of localKeyResults) {
        const match = serverKeyResults.find(
          (kr: any) =>
            kr.title === localKr.title &&
            Number(kr.target_value) === Number(localKr.target_value) &&
            kr.unit === localKr.unit
        );
        if (match?.id) {
          await markKeyResultSynced(localKr.id, match.id);
        }
      }
    } catch (error) {
      // Se falhar o detalhamento, mantem objetivo sincronizado e tentara atualizar KRs depois.
    }
    return { success: true };
  }

  if (item.method === 'PATCH' || item.method === 'PUT') {
    const payloadData = await buildObjectiveUpdatePayload(localId);
    if (payloadData?.missing) return { skip: true };
    const { objective, payload } = payloadData as { objective: any; payload: any };
    if (shouldSkipSynced(objective)) return { skip: true };
    const serverId = objective.server_id || item.entity?.server_id;
    if (!serverId) return { defer: true };
    await api.patch(`/api/v1/objectives/${serverId}`, payload);
    await markObjectiveSynced(localId, serverId);
    return { success: true };
  }

  return { skip: true };
}

async function processKeyResultItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (!localId) return { skip: true };
  const payloadData = await buildKeyResultPayload(localId);
  if (payloadData?.missing) return { skip: true };
  const { keyResult, payload } = payloadData as { keyResult: any; payload: any };
  if (shouldSkipSynced(keyResult)) return { skip: true };
  const serverId = keyResult.server_id || item.entity?.server_id;
  if (!serverId) return { defer: true };
  await api.patch(`/api/v1/key-results/${serverId}`, payload);
  await markKeyResultSynced(localId, serverId);
  return { success: true };
}

async function processIndicatorItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (!localId) return { skip: true };
  const payloadData = await buildIndicatorPayload(localId);
  if (payloadData?.missing) return { skip: true };
  const { indicator, payload } = payloadData as { indicator: any; payload: any };
  if (shouldSkipSynced(indicator)) return { skip: true };
  const keyResultServerId = await resolveKeyResultServerId(indicator.key_result_id);
  if (!keyResultServerId) return { defer: true };
  const response = await api.post(`/api/v1/key-results/${keyResultServerId}/indicators`, payload);
  if (response.data?.id) {
    await markIndicatorSynced(localId, response.data.id);
  }
  return { success: true };
}

async function processInvestmentPlanItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (item.method === 'DELETE') {
    const serverId = item.entity?.server_id;
    if (!serverId) return { skip: true };
    await api.delete(`/api/v1/investment-plans/${serverId}`);
    return { success: true };
  }
  if (!localId) return { skip: true };
  const payloadData = await buildInvestmentPlanPayload(localId);
  if (payloadData?.missing) return { skip: true };
  const { plan, payload } = payloadData as { plan: any; payload: any };
  if (shouldSkipSynced(plan)) return { skip: true };
  const serverId = plan.server_id || item.entity?.server_id;
  if (item.method === 'POST') {
    if (plan.server_id) return { success: true };
    const response = await api.post('/api/v1/investment-plans', payload);
    if (response.data?.id) {
      await markInvestmentPlanSynced(localId, response.data.id);
    }
    return { success: true };
  }
  if (!serverId) return { defer: true };
  await api.put(`/api/v1/investment-plans/${serverId}`, payload);
  await markInvestmentPlanSynced(localId, serverId);
  return { success: true };
}

async function processInvestmentScenarioItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (!localId) return { skip: true };
  const payloadData = await buildInvestmentScenarioPayload(localId);
  if (payloadData?.missing) return { skip: true };
  if (payloadData?.defer) return { defer: true };
  const { scenario, payload } = payloadData as { scenario: any; payload: any };
  if (shouldSkipSynced(scenario)) return { skip: true };
  if (item.method !== 'POST') return { skip: true };
  const response = await api.post('/api/v1/investment-advisory/scenarios', payload);
  if (response.data?.id) {
    await markInvestmentScenarioSynced(localId, response.data.id);
  }
  return { success: true };
}

async function processInvestmentLedgerItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (!localId) return { skip: true };
  const payloadData = await buildInvestmentLedgerPayload(localId);
  if (payloadData?.missing) return { skip: true };
  const { ledger, payload } = payloadData as { ledger: any; payload: any };
  if (shouldSkipSynced(ledger)) return { skip: true };
  if (item.method !== 'POST') return { skip: true };
  const response = await api.post('/api/v1/investment-tracking/ledgers', payload);
  if (response.data?.id) {
    await markInvestmentLedgerSynced(localId, response.data.id);
  }
  return { success: true };
}

async function processInvestmentRecordItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (!localId) return { skip: true };
  const payloadData = await buildInvestmentRecordPayload(localId);
  if (payloadData?.missing) return { skip: true };
  if (payloadData?.defer) return { defer: true };
  const { record, payload } = payloadData as { record: any; payload: any };
  if (shouldSkipSynced(record)) return { skip: true };
  if (item.method !== 'POST') return { skip: true };
  const response = await api.post('/api/v1/investment-tracking/records', payload);
  if (response.data?.id) {
    await markInvestmentRecordSynced(localId, response.data.id);
  }
  return { success: true };
}

async function processInvestmentAllocationItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (!localId) return { skip: true };
  const payloadData = await buildInvestmentAllocationPayload(localId);
  if (payloadData?.missing) return { skip: true };
  const { allocation, payload } = payloadData as { allocation: any; payload: any };
  if (shouldSkipSynced(allocation)) return { skip: true };
  if (item.method !== 'POST') return { skip: true };
  const response = await api.post('/api/v1/investment-advisory/allocation', payload);
  if (response.data?.id) {
    await markInvestmentAllocationSynced(localId, response.data.id);
  }
  return { success: true };
}

async function processInvestmentGoalItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (!localId) return { skip: true };
  const payloadData = await buildInvestmentGoalPayload(localId);
  if (payloadData?.missing) return { skip: true };
  if (payloadData?.defer) return { defer: true };
  const { goal, payload } = payloadData as { goal: any; payload: any };
  if (shouldSkipSynced(goal)) return { skip: true };
  if (item.method !== 'POST') return { skip: true };
  const response = await api.post('/api/v1/investment-advisory/goals', payload);
  if (response.data?.id) {
    await markInvestmentGoalSynced(localId, response.data.id);
  }
  return { success: true };
}

async function processAccountItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (item.method === 'DELETE') {
    const serverId = item.entity?.server_id;
    if (!serverId) return { skip: true };
    await api.delete(`/api/v1/accounts/${serverId}`);
    return { success: true };
  }
  if (!localId) return { skip: true };
  const payloadData = await buildAccountPayload(localId);
  if (payloadData?.missing) return { skip: true };
  const { account, payload } = payloadData as { account: any; payload: any };
  if (shouldSkipSynced(account)) return { skip: true };
  const serverId = account.server_id || item.entity?.server_id;

  if (item.method === 'POST') {
    if (account.server_id) return { success: true };
    const response = await api.post('/api/v1/accounts', payload);
    if (response.data?.id) {
      await markAccountSynced(localId, response.data.id);
    }
    return { success: true };
  }

  const effectiveServerId = serverId;
  if (!effectiveServerId) return { defer: true };

  await api.request({
    method: item.method,
    url: `/api/v1/accounts/${effectiveServerId}`,
    data: payload,
  });
  await markAccountSynced(localId, effectiveServerId);
  return { success: true };
}

async function processCategoryItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (item.method === 'DELETE') {
    const serverId = item.entity?.server_id;
    if (!serverId) return { skip: true };
    await api.delete(`/api/v1/categories/${serverId}`);
    return { success: true };
  }
  if (!localId) return { skip: true };
  const payloadData = await buildCategoryPayload(localId);
  if (payloadData?.missing) return { skip: true };
  const { category, payload } = payloadData as { category: any; payload: any };
  if (shouldSkipSynced(category)) return { skip: true };
  const serverId = category.server_id || item.entity?.server_id;

  if (item.method === 'POST') {
    if (category.server_id) return { success: true };
    const response = await api.post('/api/v1/categories', payload);
    if (response.data?.id) {
      await markCategorySynced(localId, response.data.id);
    }
    return { success: true };
  }

  const effectiveServerId = serverId;
  if (!effectiveServerId) return { defer: true };

  await api.request({
    method: item.method,
    url: `/api/v1/categories/${effectiveServerId}`,
    data: payload,
  });
  await markCategorySynced(localId, effectiveServerId);
  return { success: true };
}

async function processTransactionItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (item.method === 'DELETE') {
    const serverId = item.entity?.server_id;
    if (!serverId) return { skip: true };
    await api.delete(`/api/v1/transactions/${serverId}`);
    return { success: true };
  }
  if (!localId) return { skip: true };
  const payloadData = await buildTransactionPayload(localId);
  if (payloadData?.missing) return { skip: true };
  if (payloadData?.defer) return { defer: true };
  const { transaction, payload } = payloadData as { transaction: any; payload: any };
  if (shouldSkipSynced(transaction)) return { skip: true };
  const serverId = transaction.server_id || item.entity?.server_id;

  if (item.method === 'POST') {
    if (transaction.server_id) return { success: true };
    const response = await api.post('/api/v1/transactions', payload);
    if (response.data?.id) {
      await markTransactionSynced(localId, response.data.id);
    }
    return { success: true };
  }

  const effectiveServerId = serverId;
  if (!effectiveServerId) return { defer: true };

  if (TRANSACTION_MARK_PAID.test(item.url)) {
    await api.put(`/api/v1/transactions/${effectiveServerId}/mark-paid`);
    await markTransactionSynced(localId, effectiveServerId);
    return { success: true };
  }

  if (TRANSACTION_MARK_UNPAID.test(item.url)) {
    await api.put(`/api/v1/transactions/${effectiveServerId}/mark-unpaid`);
    await markTransactionSynced(localId, effectiveServerId);
    return { success: true };
  }

  await api.request({
    method: item.method,
    url: `/api/v1/transactions/${effectiveServerId}`,
    data: payload,
  });
  await markTransactionSynced(localId, effectiveServerId);
  return { success: true };
}

async function processTaskItem(item: SyncQueueItem, api: ReturnType<typeof axios.create>) {
  const localId = item.entity?.local_id ?? parseLocalIdFromUrl(item.url);
  if (item.method === 'DELETE') {
    const serverId = item.entity?.server_id;
    if (!serverId) return { skip: true };
    await api.delete(`/api/v1/tasks/${serverId}`);
    return { success: true };
  }
  if (!localId) return { skip: true };
  const payloadData = await buildTaskPayload(localId);
  if (payloadData?.missing) return { skip: true };
  if (payloadData?.defer) return { defer: true };
  const { task, payload } = payloadData as { task: any; payload: any };
  if (shouldSkipSynced(task)) return { skip: true };
  const serverId = task.server_id || item.entity?.server_id;

  if (item.method === 'POST') {
    if (task.server_id) return { success: true };
    const response = await api.post('/api/v1/tasks', payload);
    if (response.data?.id) {
      await markTaskSynced(localId, response.data.id);
    }
    return { success: true };
  }

  const effectiveServerId = serverId;
  if (!effectiveServerId) return { defer: true };

  if (TASK_COMPLETE.test(item.url)) {
    await api.put(`/api/v1/tasks/${effectiveServerId}/complete`);
    await markTaskSynced(localId, effectiveServerId);
    return { success: true };
  }

  if (TASK_UNCOMPLETE.test(item.url)) {
    await api.put(`/api/v1/tasks/${effectiveServerId}/uncomplete`);
    await markTaskSynced(localId, effectiveServerId);
    return { success: true };
  }

  await api.request({
    method: item.method,
    url: `/api/v1/tasks/${effectiveServerId}`,
    data: payload,
  });
  await markTaskSynced(localId, effectiveServerId);
  return { success: true };
}

export async function processSyncQueue() {
  const token = localStorage.getItem('token');
  if (!token) {
    return { success: false, reason: 'no-auth' };
  }

  const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
  });

  const items = await listPendingSyncItems();
  for (const item of items) {
    if (!item.id) continue;
    if (item.attempts >= MAX_ATTEMPTS) {
      if (item.status !== 'failed') {
        item.status = 'failed';
        item.last_error = item.last_error || 'Max attempts reached';
        await updateSyncItem(item);
      }
      continue;
    }

    try {
      let result: { success?: boolean; defer?: boolean; skip?: boolean } | null = null;

      if (RECURRING_ENDPOINT.test(item.url) || RECURRING_DELETE_SCOPE.test(item.url) || item.entity?.type === 'recurring_transactions') {
        result = await processRecurringItem(item, api);
      } else if (ACCOUNT_ENDPOINT.test(item.url) || item.entity?.type === 'accounts') {
        result = await processAccountItem(item, api);
      } else if (CATEGORY_ENDPOINT.test(item.url) || item.entity?.type === 'categories') {
        result = await processCategoryItem(item, api);
      } else if (
        TRANSACTION_ENDPOINT.test(item.url) ||
        TRANSACTION_MARK_PAID.test(item.url) ||
        TRANSACTION_MARK_UNPAID.test(item.url) ||
        item.entity?.type === 'transactions'
      ) {
        result = await processTransactionItem(item, api);
      } else if (
        TASK_ENDPOINT.test(item.url) ||
        TASK_COMPLETE.test(item.url) ||
        TASK_UNCOMPLETE.test(item.url) ||
        item.entity?.type === 'tasks'
      ) {
        result = await processTaskItem(item, api);
      } else if (OBJECTIVE_ENDPOINT.test(item.url) || item.entity?.type === 'objectives') {
        result = await processObjectiveItem(item, api);
      } else if (KEY_RESULT_ENDPOINT.test(item.url) || item.entity?.type === 'key_results') {
        result = await processKeyResultItem(item, api);
      } else if (INDICATOR_ENDPOINT.test(item.url) || item.entity?.type === 'indicators') {
        result = await processIndicatorItem(item, api);
      } else if (INVESTMENT_PLAN_ENDPOINT.test(item.url) || item.entity?.type === 'investment_plans') {
        result = await processInvestmentPlanItem(item, api);
      } else if (INVESTMENT_SCENARIO_ENDPOINT.test(item.url) || item.entity?.type === 'investment_scenarios') {
        result = await processInvestmentScenarioItem(item, api);
      } else if (INVESTMENT_LEDGER_ENDPOINT.test(item.url) || item.entity?.type === 'investment_ledgers') {
        result = await processInvestmentLedgerItem(item, api);
      } else if (INVESTMENT_RECORD_ENDPOINT.test(item.url) || item.entity?.type === 'investment_records') {
        result = await processInvestmentRecordItem(item, api);
      } else if (INVESTMENT_ALLOCATION_ENDPOINT.test(item.url) || item.entity?.type === 'investment_allocations') {
        result = await processInvestmentAllocationItem(item, api);
      } else if (INVESTMENT_GOAL_ENDPOINT.test(item.url) || item.entity?.type === 'investment_goals') {
        result = await processInvestmentGoalItem(item, api);
      }

      if (result?.defer) {
        continue;
      }
      if (result?.skip) {
        await deleteSyncItem(item.id);
        continue;
      }
      if (result?.success) {
        await deleteSyncItem(item.id);
        continue;
      }

      await deleteSyncItem(item.id);
    } catch (error: any) {
      item.attempts += 1;
      item.last_attempt_at = new Date().toISOString();
      item.last_error = error?.message || String(error);
      if (item.attempts >= MAX_ATTEMPTS) {
        item.status = 'failed';
      }
      await updateSyncItem(item);
    }
  }

  return { success: true };
}
